import React, { useState, useEffect } from 'react';

import { Modal, Input, message, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import PermissionMatrix from './PermissionMatrix';

const { Text } = Typography;

/**
 * 建立權限範本 Modal
 *
 * @param {Boolean} open - Modal 開關
 * @param {Object} templateService - injected service ({ upsert, ... })
 * @param {String} role - 'operator' | 'admin' (used for permission-matrix whitelist lookup only)
 * @param {Function} onClose - 關閉回調
 * @param {Function} onCreated - 建立完成回調 (template) => void
 */
const CreateTemplateModal = ({ open, templateService, role, onClose, onCreated }) => {
  const { t } = useTranslation();

  const [templateName, setTemplateName] = useState('');
  // 新範本預設全部勾選（對應 spec 3.3）
  const [permissions, setPermissions] = useState(['*:*']);
  const [saving, setSaving] = useState(false);

  // 每次開啟時重置狀態（避免上次關閉殘留）
  useEffect(() => {
    if (open) {
      setTemplateName('');
      setPermissions(['*:*']);
    }
  }, [open]);

  const handleSave = async () => {
    const name = templateName.trim();
    if (!name) return;

    try {
      setSaving(true);
      const result = await templateService.upsert({
        name,
        permissions,
      });

      if (result.success !== false) {
        message.success(t('common.permissions_editor.save_template_success'));
        onCreated(result.data || { name, permissions });
        handleReset();
      } else {
        message.error(t('common.permissions_editor.save_template_failed'));
      }
    } catch (error) {
      console.error('Failed to create template:', error);
      message.error(t('common.permissions_editor.save_template_failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setTemplateName('');
    setPermissions([]);
    onClose();
  };

  return (
    <Modal
      title={t('common.template_select.create_template')}
      open={open}
      onOk={handleSave}
      onCancel={handleReset}
      confirmLoading={saving}
      okText={t('common.action.confirm')}
      cancelText={t('common.action.cancel')}
      okButtonProps={{ disabled: !templateName.trim() }}
      width={800}
      styles={{ body: { maxHeight: '60vh', overflowY: 'auto' } }}
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>{t('common.permissions_editor.template_name_placeholder')}</Text>
        <Input
          style={{ marginTop: 8 }}
          placeholder={t('common.permissions_editor.template_name_placeholder')}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          maxLength={50}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
          {t('common.permissions_editor.save_as_template_overwrite_hint')}
        </Text>
      </div>

      <PermissionMatrix
        value={permissions}
        onChange={setPermissions}
        role={role}
      />
    </Modal>
  );
};

export default CreateTemplateModal;
